import { useEffect } from 'react';
import Widget from 'components/Dashboard/Widget';
import { useKPIGetResourceUtilization } from 'api/kpi/kpi';
import { KPIsWidgetEnum, LongTitles } from '../../utils';
import useManagementKpiFilter from './useManagementKpiFilter';
import {
  ControlFigures,
  nf,
  pct,
  tableStyle,
  tdNumStyle,
  tdStyle,
  thNumStyle,
  thStyle,
  totalRowStyle,
  WidgetState,
} from './shared';

/**
 * P6 och P7 — resursutnyttjande mot arbetstidstaket.
 *
 * Båda nämnarna visas. Skillnaden mellan dem är själva insikten: samma vecka kan vara 90 %
 * mot anmäld tid och 78 % mot taket.
 *
 * OBS: rapportveckan går fredag–torsdag och spänner därför över två ISO-veckor. Med
 * veckogruppering returnerar servern två rader — veckotalet är summan, så widgeten summerar
 * per affärsenhet i stället för att visa raderna rakt av.
 */
const ResourceUtilizationWidget = () => {
  const { isReady, isDateRangeTooLong, filter, deps } = useManagementKpiFilter();
  const getData = useKPIGetResourceUtilization();

  useEffect(() => {
    if (isReady) {
      getData.mutate({ data: filter });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  const result = getData.data;

  const byUnit = new Map<
    string,
    { fieldTesters: number; cap: number; reported: number; worked: number }
  >();
  (result?.dataPoints ?? []).forEach((dp) => {
    const current = byUnit.get(dp.businessUnitName) ?? {
      fieldTesters: 0,
      cap: 0,
      reported: 0,
      worked: 0,
    };
    current.fieldTesters = Math.max(current.fieldTesters, dp.fieldTesters);
    current.cap += dp.capHours;
    current.reported += dp.reportedAvailableHours;
    current.worked += dp.workedHours;
    byUnit.set(dp.businessUnitName, current);
  });

  const rows = Array.from(byUnit.entries())
    .map(([name, v]) => ({
      name,
      ...v,
      ofCap: v.cap > 0 ? (v.worked / v.cap) * 100 : null,
      ofReported: v.reported > 0 ? (v.worked / v.reported) * 100 : null,
    }))
    .sort((a, b) => b.cap - a.cap);

  return (
    <Widget
      title={LongTitles[KPIsWidgetEnum.ResourceUtilization]}
      count={Math.round(result?.utilizationOfCapPercentage ?? 0)}
      isLoading={getData.isLoading}
      info="Arbetade timmar mot arbetstidstaket, med fälttestarnas egen anmälda tillgänglighet som jämförelsetal. Taket är veckotimmar gånger sysselsättningsgrad, minskat med godkänd frånvaro. Veckovila enligt körpolicyn räknas inte som frånvaro."
    >
      <WidgetState
        isLoading={getData.isLoading}
        isDateRangeTooLong={isDateRangeTooLong}
        isEmpty={rows.length === 0}
      >
        <p style={{ textAlign: 'center', marginBottom: '10px' }}>
          Mot taket <strong>{pct(result?.utilizationOfCapPercentage)}</strong> · Mot anmäld
          tid <strong>{pct(result?.utilizationOfReportedPercentage)}</strong>
        </p>

        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={thStyle}>Affärsenhet</th>
              <th style={thNumStyle}>FT</th>
              <th style={thNumStyle}>Tak (h)</th>
              <th style={thNumStyle}>Anmält (h)</th>
              <th style={thNumStyle}>Arbetat (h)</th>
              <th style={thNumStyle}>Mot tak</th>
              <th style={thNumStyle}>Mot anmält</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.name} style={{ borderBottom: '1px solid #eee' }}>
                <td style={tdStyle}>{row.name}</td>
                <td style={tdNumStyle}>{nf(row.fieldTesters)}</td>
                <td style={tdNumStyle}>{nf(row.cap, 1)}</td>
                <td style={tdNumStyle}>{nf(row.reported, 1)}</td>
                <td style={tdNumStyle}>{nf(row.worked, 1)}</td>
                <td style={tdNumStyle}>{pct(row.ofCap)}</td>
                <td style={tdNumStyle}>{pct(row.ofReported)}</td>
              </tr>
            ))}
            <tr style={totalRowStyle}>
              <td style={tdStyle}>Totalt</td>
              <td style={tdNumStyle}>{nf(result?.fieldTesters ?? 0)}</td>
              <td style={tdNumStyle}>{nf(result?.capHours ?? 0, 1)}</td>
              <td style={tdNumStyle}>{nf(result?.reportedAvailableHours ?? 0, 1)}</td>
              <td style={tdNumStyle}>{nf(result?.workedHours ?? 0, 1)}</td>
              <td style={tdNumStyle}>{pct(result?.utilizationOfCapPercentage)}</td>
              <td style={tdNumStyle}>{pct(result?.utilizationOfReportedPercentage)}</td>
            </tr>
          </tbody>
        </table>

        <ControlFigures
          items={[
            `Tak ${nf(result?.capHoursPerFullTimeWeek ?? 0)} h per heltidsvecka`,
            result && result.fieldTestersWithoutEmploymentLevel > 0
              ? `${nf(result.fieldTestersWithoutEmploymentLevel)} FT saknar sysselsättningsgrad och ingår inte`
              : null,
            result && !result.fieldTesterRoleResolved
              ? 'Rollen Fälttestare kunde inte hittas — utnyttjandet går inte att räkna, det betyder inte att ingen arbetat'
              : null,
            result && result.fieldTestersExcludedBySupplier > 0
              ? `${nf(
                  result.fieldTestersExcludedBySupplier
                )} FT är dedikerade till en annan leverantör och ingår inte`
              : null,
            result && result.fieldTestersInactiveInPeriod > 0
              ? `${nf(
                  result.fieldTestersInactiveInPeriod
                )} FT var inte i tjänst i perioden och ingår inte`
              : null,
            result && result.fieldTestersWithoutAvailability > 0
              ? `${nf(
                  result.fieldTestersWithoutAvailability
                )} FT körde utan att ha anmält tillgänglighet`
              : null,
            result && result.hoursAwaitingApproval > 0
              ? `${nf(result.hoursAwaitingApproval, 1)} h väntar på godkännande och kan ändras`
              : null,
          ]}
        />
      </WidgetState>
    </Widget>
  );
};

export default ResourceUtilizationWidget;
